import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";

import DataTable from "@/components/mui/DataTable";

export default function AdminReports() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // paginação tabela (client-side)
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/companies`),
        axios.get(`${API}/admin/stats`),
      ]);
      setCompanies(companiesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar relatórios");
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value) =>
    `R$ ${Number(value || 0).toFixed(2).replace(".", ",")}`;

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && companies.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const activeCompanies = companies.filter((company) => company.active);

  const companiesPaid = stats?.companies_paid || 0;
  const companiesInTrial = stats?.companies_in_trial || 0;
  const companiesOverdue = stats?.companies_overdue || 0;

  const conversionRate =
    companiesPaid + companiesInTrial > 0
      ? (
          (companiesPaid / (companiesPaid + companiesInTrial)) *
          100
        ).toFixed(1)
      : "0.0";

  const summaryCards = [
    {
      key: "current_revenue",
      title: "Receita Mensal Atual",
      value: formatMoney(stats?.monthly_revenue || 0),
      subtitle: `${companiesPaid} empresas pagantes`,
      icon: <TrendingUpIcon fontSize="small" color="success" />,
      valueColor: "success.main",
      subtitleColor: "success.main",
    },
    {
      key: "potential_revenue",
      title: "Receita Potencial",
      value: formatMoney(companiesInTrial * 90.00),
      subtitle: `${companiesInTrial} em período de teste`,
      icon: null,
      valueColor: "text.primary",
      subtitleColor: "text.secondary",
    },
    {
      key: "risk_revenue",
      title: "Receita em Risco",
      value: formatMoney(companiesOverdue * 90.00),
      subtitle: `${companiesOverdue} empresas em atraso`,
      icon: <TrendingDownIcon fontSize="small" color="error" />,
      valueColor: "error.main",
      subtitleColor: "error.main",
    },
    {
      key: "conversion_rate",
      title: "Taxa de Conversão",
      value: `${conversionRate}%`,
      subtitle: "teste → pagante",
      icon: null,
      valueColor: "text.primary",
      subtitleColor: "text.secondary",
    },
  ];

  // fatia local dos registros para a página atual
  const paginatedCompanies =
    rowsPerPage > 0
      ? activeCompanies.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        )
      : activeCompanies;

  // ===== colunas da DataTable =====
  const columns = [
    {
      id: "name",
      label: "Empresa",
      render: (company) => (
        <Typography variant="body2" fontWeight={600}>
          {company.name}
        </Typography>
      ),
    },
    {
      id: "payment_status",
      label: "Status",
      render: (company) => {
        const status = company.payment_status;
        const label =
          status === "periodo_teste"
            ? "Teste"
            : status === "em_dia"
            ? "Em Dia"
            : status === "em_atraso"
            ? "Atrasado"
            : "Bloqueado";

        const color =
          status === "em_dia"
            ? "success"
            : status === "periodo_teste"
            ? "warning"
            : "error";

        return (
          <Chip
            label={label}
            color={color}
            size="small"
          />
        );
      },
    },
    {
      id: "due_date",
      label: "Próximo Vencimento",
      render: (company) => {
        if (company.payment_due_date) {
          return new Date(
            company.payment_due_date
          ).toLocaleDateString("pt-BR");
        }
        if (company.trial_ends_at) {
          return new Date(
            company.trial_ends_at
          ).toLocaleDateString("pt-BR");
        }
        return "-";
      },
    },
    {
      id: "last_payment_date",
      label: "Último Pagamento",
      render: (company) =>
        company.last_payment_date
          ? new Date(
              company.last_payment_date
            ).toLocaleDateString("pt-BR")
          : "-",
    },
    {
      id: "monthly_value",
      label: "Valor Mensal",
      align: "right",
      render: () => (
        <Typography variant="body2" fontWeight={600}>
          {formatMoney(90.00)}
        </Typography>
      ),
    },
  ];

  const tablePagination = {
    page,
    rowsPerPage,
    total: activeCompanies.length,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [5, 10, 25, 50, { label: "Todos", value: -1 }],
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Relatórios Financeiros
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visão geral das finanças do sistema
          </Typography>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 3,
          mb: 4,
        }}
      >
        {summaryCards.map((card) => (
          <Card
            key={card.key}
            sx={{
              height: "100%",
              borderRadius: 2,
              boxShadow: "0 10px 40px rgba(15,23,42,0.04)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
              >
                {card.title}
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  color: card.valueColor,
                  fontSize: { xs: "1.8rem", md: "2.1rem" },
                }}
              >
                {card.value}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                {card.icon}
                <Typography
                  variant="caption"
                  sx={{ color: card.subtitleColor }}
                >
                  {card.subtitle}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Tabela de empresas usando DataTable */}
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0 10px 40px rgba(15,23,42,0.04)",
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Status Financeiro por Empresa
          </Typography>

          <DataTable
            columns={columns}
            rows={paginatedCompanies}
            loading={loading}
            pagination={tablePagination}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `admin-report-company-${row.id}`}
            emptyMessage="Nenhuma empresa encontrada."
          />
        </CardContent>
      </Card>
    </Box>
  );
}
